// Clean, honest memory list — renders the ACTUAL CogniMemo memories (not the old
// synthetic company/dept/employee/round/tool graph). Each memory is a card with its
// fact-type color + label + text + time, grouped by fact type. A fact-type filter
// row + search keep it easy to scan. This is the real data model: typed memories.
import { useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import type { MorrowMemory } from "../../morrowMemory";
import { FACT_TYPE_META, factTypeColor } from "./forceGraphModel";

const typeLabel = (type?: string | null): string => {
  const t = (type ?? "").trim().toLowerCase();
  return FACT_TYPE_META.find((f) => f.type === t)?.label ?? (t ? t : "Memory");
};

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function MemoryList({ memories }: { readonly memories: readonly MorrowMemory[] }) {
  const [query, setQuery] = useState("");
  const [activeType, setActiveType] = useState<string | null>(null);

  // Only show fact-type chips that actually have memories (+ keep it honest).
  const presentTypes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of memories) {
      const t = (m.type ?? "").trim().toLowerCase() || "other";
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    return FACT_TYPE_META.filter((f) => counts.has(f.type)).map((f) => ({
      ...f,
      count: counts.get(f.type) ?? 0,
    }));
  }, [memories]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return memories.filter((m) => {
      if (activeType && (m.type ?? "").trim().toLowerCase() !== activeType) return false;
      if (q && !m.text.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [memories, query, activeType]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search memories…"
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground/30"
        />
        {presentTypes.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setActiveType(null)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium transition",
                activeType === null
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              All · {memories.length}
            </button>
            {presentTypes.map((f) => (
              <button
                key={f.type}
                type="button"
                onClick={() => setActiveType(activeType === f.type ? null : f.type)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition",
                  activeType === f.type
                    ? "border-foreground text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="size-2 rounded-full" style={{ backgroundColor: f.color }} />
                {f.label} · {f.count}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-lg border border-border/60 bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
          No memories match.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {filtered.map((m) => (
            <li
              key={m.id}
              className="group rounded-xl border border-border/70 bg-card px-4 py-3 transition hover:border-border hover:shadow-sm"
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: factTypeColor(m.type) }}
                  aria-hidden
                />
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  {typeLabel(m.type)}
                </span>
                {m.createdAt ? (
                  <span className="ml-auto text-[11px] tabular-nums text-muted-foreground/70">
                    {formatWhen(m.createdAt)}
                  </span>
                ) : null}
              </div>
              <p className="mt-1.5 text-sm leading-6 text-foreground">{m.text}</p>
              {m.department || m.ownerEmail ? (
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                  {m.department ? (
                    <span className="rounded bg-muted px-1.5 py-0.5">{m.department}</span>
                  ) : null}
                  {m.ownerEmail ? (
                    <span className="rounded bg-muted px-1.5 py-0.5">{m.ownerEmail}</span>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
